import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { showToast } from "./Toast";

const IntegrationSetupWizard = ({ integration, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [setupProgress, setSetupProgress] = useState({
    permissions: false,
    connection: false,
    test: false,
  });

  // Define setup steps based on integration type
  const getSteps = () => {
    const baseSteps = [
      {
        id: "permissions",
        title: "Grant Permissions",
        description: `Allow SwitchrAgent to access your ${integration.name} account`,
        icon: "🔐",
      },
      {
        id: "connection",
        title: "Connect Account",
        description: "Authenticate and connect your account",
        icon: "🔗",
      },
      {
        id: "test",
        title: "Test Connection",
        description: "Verify the integration is working correctly",
        icon: "✅",
      },
    ];

    return baseSteps;
  };

  const steps = getSteps();

  const handleNext = () => {
    const step = steps[currentStep];

    // Mark current step as complete
    setSetupProgress({
      ...setupProgress,
      [step.id]: true,
    });

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // All steps complete
      showToast.success(`${integration.name} connected successfully!`);
      onComplete(integration);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    // Save partial progress
    localStorage.setItem(
      `integration_setup_${integration.id}`,
      JSON.stringify({ step: currentStep, progress: setupProgress })
    );
    showToast.info("Setup progress saved. You can resume later.");
    onClose();
  };

  const StepContent = ({ step }) => {
    switch (step.id) {
      case "permissions":
        return (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Required Permissions:</h4>
              <ul className="space-y-2 text-sm text-blue-800">
                {integration.id === "google-calendar" && (
                  <>
                    <li>✓ Read and write calendar events</li>
                    <li>✓ View calendar availability</li>
                    <li>✓ Create and manage meetings</li>
                  </>
                )}
                {integration.id === "slack" && (
                  <>
                    <li>✓ Send messages to channels</li>
                    <li>✓ Read channel messages</li>
                    <li>✓ Manage channel notifications</li>
                  </>
                )}
                {integration.id === "gmail" && (
                  <>
                    <li>✓ Read and send emails</li>
                    <li>✓ Manage email labels</li>
                    <li>✓ Access email metadata</li>
                  </>
                )}
                {!["google-calendar", "slack", "gmail"].includes(integration.id) && (
                  <>
                    <li>✓ Read account data</li>
                    <li>✓ Write and update records</li>
                    <li>✓ Access API endpoints</li>
                  </>
                )}
              </ul>
            </div>
            <p className="text-sm text-gray-600">
              These permissions allow SwitchrAgent to automate workflows using your {integration.name} account.
            </p>
          </div>
        );

      case "connection":
        return (
          <div className="space-y-4">
            <div className="text-center py-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <span className="text-3xl">{integration.icon}</span>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">
                Connect to {integration.name}
              </h4>
              <p className="text-gray-600 mb-6">
                Click the button below to securely authenticate with {integration.name}
              </p>
              <button
                onClick={() => {
                  // Simulate OAuth flow
                  showToast.info(`Opening ${integration.name} authentication...`);
                  setTimeout(() => {
                    handleNext();
                  }, 1500);
                }}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Connect {integration.name} Account
              </button>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-xs text-gray-600">
                <strong>🔒 Secure OAuth 2.0:</strong> We use industry-standard OAuth 2.0 for authentication.
                Your credentials are never stored on our servers.
              </p>
            </div>
          </div>
        );

      case "test":
        return (
          <div className="space-y-4">
            <div className="text-center py-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <span className="text-3xl">✅</span>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Connection Successful!</h4>
              <p className="text-gray-600 mb-4">
                Your {integration.name} account is now connected and ready to use in workflows.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h5 className="font-medium text-gray-900 mb-3">Connection Details:</h5>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className="text-green-600 font-medium">✓ Connected</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Account:</span>
                  <span className="text-gray-900">user@example.com</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Connected:</span>
                  <span className="text-gray-900">{new Date().toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                💡 You can now use {integration.name} in your workflows. Try creating a new workflow or
                adding {integration.name} actions to existing ones.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold">Setup {integration.name}</h2>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
            >
              ✕
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                      index < currentStep
                        ? "bg-green-500 text-white"
                        : index === currentStep
                        ? "bg-white text-blue-600"
                        : "bg-white bg-opacity-30 text-white"
                    }`}
                  >
                    {setupProgress[step.id] ? "✓" : index + 1}
                  </div>
                  <span className="text-xs mt-2 text-center hidden sm:block">{step.title}</span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`h-1 flex-1 mx-2 rounded transition-colors ${
                      index < currentStep ? "bg-green-500" : "bg-white bg-opacity-30"
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: "calc(90vh - 250px)" }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-3xl">{steps[currentStep].icon}</span>
                  <h3 className="text-xl font-bold text-gray-900">
                    {steps[currentStep].title}
                  </h3>
                </div>
                <p className="text-gray-600">{steps[currentStep].description}</p>
              </div>

              <StepContent step={steps[currentStep]} />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
          <div className="flex gap-2">
            {currentStep > 0 && (
              <button
                onClick={handlePrevious}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors font-medium"
              >
                ← Previous
              </button>
            )}
            <button
              onClick={handleSkip}
              className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors text-sm"
            >
              Save & Resume Later
            </button>
          </div>

          <button
            onClick={handleNext}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            {currentStep === steps.length - 1 ? "Complete Setup" : "Continue →"}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default IntegrationSetupWizard;
